import { OPEN_TASK_STATUSES } from './taskHelpers'
import { toTaskFilter } from '@/composables/services/taskFilter'
import type { TaskSearchRequest } from '@/composables/services/useTasksApi'

function endOfTodayIso(): string {
  const d = new Date()
  d.setHours(23, 59, 59, 999)
  return d.toISOString()
}

export interface TaskViewContext {
  userId: string
  orgId: string
  teamIds: string[]
}

/** A default filter pill seed for a task view (id, operator, value). */
export interface TaskFilterSeed {
  id: string
  operator: string
  value: any
}

/**
 * Returns the default filter pill seeds for a task view.
 * These are seeded into the filter toolbar so they appear as visible pills
 * the user can read and remove.
 */
export function buildTaskViewFilters(
  viewId: 'my-tasks' | 'my-tasks-due-today' | 'team-tasks' | 'all-tasks',
  ctx: TaskViewContext,
): TaskFilterSeed[] {
  const statusSeed: TaskFilterSeed = { id: 'status', operator: 'in', value: OPEN_TASK_STATUSES }
  const ownerUserSeed: TaskFilterSeed = { id: 'ownerUser', operator: 'eq', value: ctx.userId }
  const ownerTeamSeed: TaskFilterSeed = { id: 'ownerTeam', operator: 'in', value: ctx.teamIds }
  const dueDateSeed: TaskFilterSeed = { id: 'dueDate', operator: 'before', value: endOfTodayIso() }
  const unassignedSeed: TaskFilterSeed = { id: 'unassigned', operator: 'eq', value: 'true' }

  switch (viewId) {
    case 'my-tasks':
      return [ownerUserSeed, statusSeed]
    case 'my-tasks-due-today':
      return [ownerUserSeed, statusSeed, dueDateSeed]
    case 'team-tasks':
      return [ownerTeamSeed, unassignedSeed, statusSeed]
    case 'all-tasks':
      return [statusSeed]
  }
}

/**
 * API filters for a view's default constraints.
 * Badge counts and the home metrics call the search endpoint directly, without
 * the toolbar, so they pass this in as `extraFilters`. The list does not: once
 * the user edits pills, only those pills are sent.
 *
 * `ownerUserId` stays a plain id. That is the shape the count queries already
 * used. Status, due date, and team use `{ operator, value }`.
 */
export function buildTaskViewDefaultFilters(
  viewId: 'my-tasks' | 'my-tasks-due-today' | 'team-tasks' | 'all-tasks',
  ctx: TaskViewContext,
): Record<string, any> {
  const status = toTaskFilter('in', OPEN_TASK_STATUSES)

  switch (viewId) {
    case 'my-tasks':
      return { ownerUserId: ctx.userId, status }
    case 'my-tasks-due-today':
      return {
        ownerUserId: ctx.userId,
        status,
        dueDate: toTaskFilter('le', endOfTodayIso()),
      }
    case 'team-tasks':
      return {
        ownerTeamId: toTaskFilter('in', ctx.teamIds),
        unassignedUser: true,
        status,
      }
    case 'all-tasks':
      return { status }
  }
}

/**
 * Builds a task search request for the given view.
 * View-specific defaults (owner, status, due date) are no longer hard-coded
 * here; they come through `extraFilters` which is built from the filter toolbar
 * pills seeded by `buildTaskViewFilters`.
 */
export function buildTaskViewRequest(
  viewId: 'my-tasks' | 'my-tasks-due-today' | 'team-tasks' | 'all-tasks',
  ctx: TaskViewContext,
  pagination: { limit: number; start: number },
  extraFilters?: Record<string, any>,
  sortOverride?: string,
): TaskSearchRequest {
  void viewId // viewId reserved for future view-specific overrides
  return {
    sort: sortOverride || 'dueDate_asc',
    pagination,
    filters: {
      orgId: ctx.orgId,
      ...extraFilters,
    },
  }
}

// Re-export toTaskFilter for convenience in tests that import from this module
export { toTaskFilter }
